import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Shield,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  Globe,
  Users,
  Box,
  Building2,
  Calculator,
  Inbox,
  RotateCcw,
} from "lucide-react";
import api from "../lib/api";
import { cn } from "../lib/utils";

const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };

const ROLE_LABELS = {
  admin: "Admin",
  pm_lead: "PM Lead",
  pm: "PM",
  developer: "Developer",
};

/**
 * Per-staff support access management. Admin only.
 *
 * Lists every staff member with a SUPPORT_TEAM role, lets the admin
 * grant them granular access:
 *   - "All products"  override → unrestricted
 *   - Per-product checkbox; when checked, "All deployments" toggle +
 *     individual deployment checkboxes
 *
 * Legacy default (no grant set) is full access — the page shows a
 * "default: full access" hint until admin saves restrictions for that user.
 */
export default function SupportAccessPage() {
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({}); // staff_id → unsaved access doc
  const [saving, setSaving] = useState(null); // staff_id currently saving

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [staffResp, prodResp] = await Promise.all([
        api.get("/support-access/staff"),
        api.get("/support-products", { params: { active_only: true } }),
      ]);
      setStaff(staffResp.data || []);
      setProducts(prodResp.data || []);
    } catch (e) {
      toast.error("Failed to load access data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const accessFor = (s) => {
    if (drafts[s.id]) return drafts[s.id];
    return s.support_access || { all_products: false, granted: [] };
  };

  const setDraft = (staffId, mutator) => {
    setDrafts((d) => {
      const current = d[staffId] || (staff.find((x) => x.id === staffId)?.support_access || { all_products: false, granted: [] });
      const next = mutator(JSON.parse(JSON.stringify(current)));
      return { ...d, [staffId]: next };
    });
  };

  const toggleAllProducts = (s) => {
    setDraft(s.id, (a) => {
      a.all_products = !a.all_products;
      return a;
    });
  };

  const toggleProduct = (s, slug) => {
    setDraft(s.id, (a) => {
      const idx = (a.granted || []).findIndex((g) => g.product_slug === slug);
      if (idx >= 0) {
        a.granted.splice(idx, 1);
      } else {
        a.granted = a.granted || [];
        a.granted.push({ product_slug: slug, all_instances: true, instances: [] });
      }
      return a;
    });
  };

  const toggleAllInstances = (s, slug) => {
    setDraft(s.id, (a) => {
      const g = (a.granted || []).find((x) => x.product_slug === slug);
      if (g) {
        g.all_instances = !g.all_instances;
        if (g.all_instances) g.instances = [];
      }
      return a;
    });
  };

  const toggleInstance = (s, slug, instSlug) => {
    setDraft(s.id, (a) => {
      const g = (a.granted || []).find((x) => x.product_slug === slug);
      if (!g) return a;
      g.all_instances = false;
      const idx = g.instances.indexOf(instSlug);
      if (idx >= 0) g.instances.splice(idx, 1);
      else g.instances.push(instSlug);
      return a;
    });
  };

  const productGranted = (a, slug) => (a.granted || []).find((g) => g.product_slug === slug);

  const saveDraft = async (s) => {
    const a = drafts[s.id];
    if (!a) return;
    try {
      setSaving(s.id);
      const { data } = await api.put(`/support-access/staff/${s.id}`, {
        all_products: !!a.all_products,
        granted: a.granted || [],
      });
      // Replace the staff row with the saved one.
      setStaff((rows) => rows.map((r) => (r.id === s.id ? { ...r, ...data } : r)));
      setDrafts((d) => { const next = { ...d }; delete next[s.id]; return next; });
      toast.success(`Access saved for ${s.name || s.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(null);
    }
  };

  const resetToDefault = async (s) => {
    if (!window.confirm(`Reset ${s.name || s.email} back to default (full access)?`)) return;
    try {
      setSaving(s.id);
      await api.delete(`/support-access/staff/${s.id}`);
      setStaff((rows) => rows.map((r) => (r.id === s.id ? { ...r, support_access: null, effective: { all_products: r.role === "admin", is_default_full: r.role !== "admin" } } : r)));
      setDrafts((d) => { const next = { ...d }; delete next[s.id]; return next; });
      toast.success("Reset to default");
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setSaving(null);
    }
  };

  const cancelDraft = (s) => {
    setDrafts((d) => { const next = { ...d }; delete next[s.id]; return next; });
  };

  return (
    <div className="space-y-5 p-6 max-w-5xl" data-testid="support-access-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-400" /> Support Team Access
          </h1>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Grant individual staff members access to specific support products
            and deployments. Restricting one user does not affect others.
            Admin always has full access (not shown here).
          </p>
        </div>
        <Button variant="outline" size="sm" className="border-zinc-700" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 text-center py-8">Loading…</p>
      ) : staff.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            No PM, PM Lead, or Developer staff found. Add them in Staff
            settings first.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {staff.map((s) => {
            const isExpanded = expandedId === s.id;
            const a = accessFor(s);
            const hasDraft = !!drafts[s.id];
            const isDefaultFull = s.effective?.is_default_full;
            const isAdmin = s.role === "admin";
            const grantedCount = (a.granted || []).length;
            return (
              <Card
                key={s.id}
                className={cn(
                  "bg-zinc-900/40 border-zinc-800 transition-colors",
                  hasDraft && "border-orange-500/40 bg-orange-950/10"
                )}
              >
                <CardContent className="p-0">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : s.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/30 text-left"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-zinc-100">{s.name || s.email}</span>
                        <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">
                          {ROLE_LABELS[s.role] || s.role}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-zinc-500 truncate">{s.email}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {isAdmin ? (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Full access (admin)</Badge>
                      ) : a.all_products ? (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">All products</Badge>
                      ) : isDefaultFull && !hasDraft ? (
                        <Badge className="bg-zinc-700/40 text-zinc-300 border-zinc-600/50">Default (full access)</Badge>
                      ) : grantedCount > 0 ? (
                        <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/30">
                          {grantedCount} product{grantedCount === 1 ? "" : "s"}
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/15 text-red-300 border-red-500/30">No access</Badge>
                      )}
                    </div>
                  </button>

                  {isExpanded && !isAdmin && (
                    <div className="border-t border-zinc-800 p-4 space-y-3 bg-zinc-900/30">
                      {/* All-products override */}
                      <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                        <div>
                          <div className="text-sm text-zinc-200 font-medium">Grant access to all products</div>
                          <div className="text-[11px] text-zinc-500">Overrides per-product list below. Use for trusted senior PMs.</div>
                        </div>
                        <Switch
                          checked={!!a.all_products}
                          onCheckedChange={() => toggleAllProducts(s)}
                        />
                      </div>

                      {/* Per-product list — disabled when all_products is on */}
                      <div className={cn(
                        "space-y-2 transition-opacity",
                        a.all_products && "opacity-40 pointer-events-none"
                      )}>
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                          Per-product access
                        </div>
                        {products.map((p) => {
                          const PIcon = PRODUCT_ICON_MAP[p.icon] || Box;
                          const granted = productGranted(a, p.slug);
                          const liveInstances = (p.instances || []).filter((i) => i.is_active !== false);
                          return (
                            <div
                              key={p.slug}
                              className={cn(
                                "rounded-lg border p-3 transition-colors",
                                granted ? "border-orange-500/40 bg-orange-950/10" : "border-zinc-800 bg-zinc-900/40"
                              )}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <PIcon className="w-4 h-4 text-zinc-400" />
                                  <span className="text-sm text-zinc-200 font-medium">{p.short_label || p.name}</span>
                                  {liveInstances.length > 0 && (
                                    <span className="text-[10px] text-zinc-500">{liveInstances.length} deployments</span>
                                  )}
                                </div>
                                <Switch
                                  checked={!!granted}
                                  onCheckedChange={() => toggleProduct(s, p.slug)}
                                />
                              </div>

                              {granted && liveInstances.length > 0 && (
                                <div className="mt-3 pl-6 border-l border-zinc-700/50 space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <div className="text-[11px] text-zinc-400">Allow all deployments</div>
                                    <Switch
                                      checked={!!granted.all_instances}
                                      onCheckedChange={() => toggleAllInstances(s, p.slug)}
                                    />
                                  </div>
                                  {!granted.all_instances && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                      {liveInstances.map((inst) => {
                                        const checked = (granted.instances || []).includes(inst.slug);
                                        return (
                                          <button
                                            key={inst.slug}
                                            type="button"
                                            onClick={() => toggleInstance(s, p.slug, inst.slug)}
                                            className={cn(
                                              "px-2 py-1 rounded border text-[11px] text-left transition-colors",
                                              checked
                                                ? "border-orange-500/50 bg-orange-500/10 text-orange-200"
                                                : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60"
                                            )}
                                          >
                                            {inst.label || inst.slug}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {!granted.all_instances && (granted.instances || []).length === 0 && (
                                    <p className="text-[10px] text-amber-400 italic">
                                      No deployments selected — they'll see no items for this product.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Save / Cancel / Reset */}
                      <div className="flex items-center gap-2 pt-2">
                        {hasDraft ? (
                          <>
                            <Button
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600"
                              disabled={saving === s.id}
                              onClick={() => saveDraft(s)}
                            >
                              <Save className="w-3.5 h-3.5 mr-1" />
                              {saving === s.id ? "Saving…" : "Save"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-700"
                              disabled={saving === s.id}
                              onClick={() => cancelDraft(s)}
                            >
                              <X className="w-3.5 h-3.5 mr-1" /> Cancel
                            </Button>
                          </>
                        ) : (
                          <span className="text-[10px] text-zinc-600 italic">No unsaved changes</span>
                        )}
                        {s.support_access && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-auto border-zinc-700 text-zinc-400 hover:text-red-300"
                            onClick={() => resetToDefault(s)}
                            disabled={saving === s.id}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
